import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { TextWithTooltip } from "@/components/text-with-tooltip"
import { Tag } from "@/components/ui/tag"
import { UserProvider } from "@/components/user-wrapper"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { appointmentDrawerAdditionalInfo_appointment$key } from "@/modules/waiting-room/components/appointment-drawer/__generated__/appointmentDrawerAdditionalInfo_appointment.graphql"
import { AppointmentPriority } from "@/modules/waiting-room/components/appointment-table/appointment-priority"
import { delayFormat } from "@/modules/waiting-room/helpers/delay-format"

const AppointmentDrawerAdditionalInfoFragment = graphql`
  fragment appointmentDrawerAdditionalInfo_appointment on Appointment {
    delay
    service
    language
    assignedTo
    urgency
    flexibility
    access
    specialty
  }
`

type AdditionalAppointmentInfoItem = {
  title: "access" | "assignedTo" | "delay" | "language" | "priority" | "service" | "specialty"
  content: React.JSX.Element | null
}

interface Props {
  appointmentRef: appointmentDrawerAdditionalInfo_appointment$key
}

const AppointmentDrawerAdditionalInfo = ({ appointmentRef }: Props) => {
  const { t } = useTranslation()

  const { assignedTo, delay, urgency, flexibility, language, access, service, specialty } =
    useFragment<appointmentDrawerAdditionalInfo_appointment$key>(
      AppointmentDrawerAdditionalInfoFragment,
      appointmentRef,
    )

  const additionalAppointmentInfoItems: AdditionalAppointmentInfoItem[] = [
    {
      title: "assignedTo",
      content: assignedTo ? (
        <div className="truncate" data-testid="assigned-to-preview-value">
          <UserProvider
            userName={assignedTo}
            render={(user) => <span className="truncate">{`${user.firstName} ${user.lastName}`}</span>}
          />
        </div>
      ) : null,
    },
    {
      title: "delay",
      content:
        delay > 0 ? (
          <Tag
            className="max-w-full"
            data-testid="delay-time"
            type="warning"
            hasIcon={false}
            label={delayFormat(delay)}
          />
        ) : null,
    },
    {
      title: "priority",
      content: (
        <AppointmentPriority
          className="grid-cols-1 truncate"
          urgency={urgency}
          flexibility={flexibility}
          data-testid="priority-preview-value"
        />
      ),
    },
    {
      title: "language",
      content: (
        <span className="truncate" data-testid="language-preview-value">
          {convertLanguageToISOCode(language)}
        </span>
      ),
    },
    {
      title: "access",
      content: (
        <TextWithTooltip data-testid="access-preview-value">
          {t(`scheduling.waitingRoom.access.${access}`)}
        </TextWithTooltip>
      ),
    },
    {
      title: "service",
      content: (
        <TextWithTooltip data-testid="service-preview-value">
          {t(`scheduling.waitingRoom.service.${service}`)}
        </TextWithTooltip>
      ),
    },
    {
      title: "specialty",
      content: (
        <TextWithTooltip data-testid="specialty-preview-value">
          {t(`scheduling.waitingRoom.serviceSpecialty.${specialty}`)}
        </TextWithTooltip>
      ),
    },
  ]

  return (
    <div className="animate-fade-in-top border-border bg-surface text-on-surface-subdued grid w-full content-start gap-4 rounded-xl border p-4 text-sm">
      {additionalAppointmentInfoItems.map(({ title, content }) =>
        content ? (
          <div key={title} className="flex items-center justify-between">
            <span className="text-on-surface truncate text-sm font-medium">
              {t(`scheduling.waitingRoom.grid.columnName.${title}`)}
            </span>
            {content}
          </div>
        ) : null,
      )}
    </div>
  )
}

export { AppointmentDrawerAdditionalInfo }
